import { ParsedUrlQuery } from 'querystring';

import React, { useMemo, useState } from 'react';

import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import FlexContainer from '@components/utils/FlexContainer';
import Note from '@components/ui/InfoNote';
import LoyaltyVerifications, {
  LoyaltyType,
} from '@components/ui/loyalty-verifications/LoyaltyVerifications';
import {
  shouldHideLoyalty,
  requiredAuthentication,
  LoginRedirectModal,
  checkLoyaltyPreviouslyValidated,
} from '@components/ui/loyalty-verifications/helpers';
import { useToggle } from '@hooks/common';
import { useAuthState } from '@contexts/auth/AuthContext';
import { useDevice } from '@contexts/device';
import { FlexibleDiv } from '@components/formStyles';
import { MAX_SETUR_POINTS_EARN } from '@constants/constants';
import { paramsToObject } from '@components/views/utils';

import {
  LoyaltyEarnBadgeWrapper,
  PackageVariationContainer,
  PackageVariationDescriptionContainer,
  VariationCaption,
  customCss,
  Button,
} from './styles';
import { PackageVariationProps } from './types';
import { getPromotionInfo } from './utils';
import {
  DesktopVerificationButtonContainer,
  MobileVerificationButtonContainer,
} from './VerificationButtonContainer';

const LoyaltyEarnBadge = dynamic(
  () => import('@components/LoyaltyBadge/LoyaltyBadge')
);
const PackageVariation = (props: PackageVariationProps) => {
  const key = props.loyaltyType ?? 'default';
  const { isDesktop, isMobile, isTablet } = useDevice();

  const promotionInfo = getPromotionInfo(props);
  const { loginInfo } = useAuthState();
  const isLoggedIn = !!loginInfo?.token;
  const needVerification = requiredAuthentication(props.loyaltyType);
  const previouslyValidated = checkLoyaltyPreviouslyValidated(key);
  const [loyaltyToValidate, setLoyaltyToValidate] = useState<LoyaltyType>();
  const [showLoginRedirectionModal, , setShowLoginRedirectionModal] =
    useToggle();

  const [verified, setVerified] = useState(false);
  const validated = previouslyValidated || verified;
  const priceShown = !(needVerification && !validated);

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const showMaxLoyaltyEarnPoints = useMemo(() => {
    return (
      !isLoggedIn &&
      (props?.seturLoyaltyInfo?.earn ?? 0) >= MAX_SETUR_POINTS_EARN
    );
  }, [isLoggedIn, props?.seturLoyaltyInfo]);

  if (
    shouldHideLoyalty(props.loyaltyType, query.utm_source as string, validated)
  )
    return null;
  const descriptionLength =
    (promotionInfo?.promotionDescription?.length ?? 0) > 40;

  const disableVariationSelection =
    (needVerification && !validated) || props?.disabled;

  const shouldShowLoginModal =
    props.loyaltyType === 'KocAilem' && !loginInfo?.b2CUser?.firstName
      ? true
      : false;
  const ShowVerificationPopUp = () => {
    if (requiredAuthentication(props.loyaltyType) && shouldShowLoginModal) {
      setShowLoginRedirectionModal(true);
    } else {
      setLoyaltyToValidate(props.loyaltyType);
    }
  };
  return (
    <>
      <PackageVariationContainer key={key}>
        {!needVerification && !validated ? (
          <>
            {!isDesktop && props.hasLoyaltyVariation ? (
              <LoyaltyEarnBadgeWrapper>
                <LoyaltyEarnBadge
                  discount={props?.seturLoyaltyInfo?.rate}
                  earn={props?.seturLoyaltyInfo?.earn}
                  showMaxLoyaltyEarn={showMaxLoyaltyEarnPoints}
                />
              </LoyaltyEarnBadgeWrapper>
            ) : null}
          </>
        ) : (
          <>
            {!isDesktop && needVerification && validated ? (
              <LoyaltyEarnBadgeWrapper>
                <LoyaltyEarnBadge
                  discount={props?.seturLoyaltyInfo?.rate}
                  earn={props?.seturLoyaltyInfo?.earn}
                  showMaxLoyaltyEarn={showMaxLoyaltyEarnPoints}
                />
              </LoyaltyEarnBadgeWrapper>
            ) : null}
          </>
        )}
        <PackageVariationDescriptionContainer validated={priceShown}>
          <FlexContainer
            direction='column'
            padding='10px'
            flex={3}
            onClick={disableVariationSelection ? undefined : props?.onClick}
            style={{
              cursor:
                (needVerification && !validated) || props?.disabled
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            <span
              style={{
                pointerEvents: needVerification && !validated ? 'none' : 'all',
                opacity: needVerification && !validated ? 0.4 : 1,
              }}
            >
              {props.children}
            </span>
            <VariationCaption length={descriptionLength}>
              {promotionInfo.promotionDescription}
            </VariationCaption>
            {needVerification && !validated ? (
              <FlexibleDiv top={isMobile ? 10 : 0}>
                <Note
                  variant='verification'
                  noMargin
                  hideIcon
                  customCss={customCss}
                >
                  <span style={{ fontSize: 10 }}>
                    İndirimli fiyatı görmek için {promotionInfo.promotionName}{' '}
                    hesabınızı doğrulayın.
                  </span>
                </Note>

                {isMobile ? (
                  <FlexibleDiv top={30}>
                    <Button
                      outline
                      onClick={() => {
                        ShowVerificationPopUp();
                      }}
                    >
                      Doğrula
                    </Button>
                  </FlexibleDiv>
                ) : null}
              </FlexibleDiv>
            ) : null}
          </FlexContainer>
          {isDesktop || isTablet ? (
            <DesktopVerificationButtonContainer
              handleShowVerificationModal={() => ShowVerificationPopUp()}
              hasLoyaltyVariation={props?.hasLoyaltyVariation}
              seturLoyaltyInfo={props?.seturLoyaltyInfo}
              validated={validated}
              needVerification={needVerification}
              priceShown={priceShown}
              tryTotalDiscountedSellingPrice={
                props?.tryTotalDiscountedSellingPrice
              }
              {...props}
            />
          ) : (
            <MobileVerificationButtonContainer
              handleShowVerificationModal={() => ShowVerificationPopUp()}
              hasLoyaltyVariation={props?.hasLoyaltyVariation}
              seturLoyaltyInfo={props?.seturLoyaltyInfo}
              validated={validated}
              needVerification={needVerification}
              priceShown={priceShown}
              tryTotalDiscountedSellingPrice={
                props?.tryTotalDiscountedSellingPrice
              }
              {...props}
            />
          )}
        </PackageVariationDescriptionContainer>
      </PackageVariationContainer>

      {loyaltyToValidate ? (
        <LoyaltyVerifications
          tourCard={props.tourCard}
          action={() => {
            setLoyaltyToValidate(undefined);
            setVerified(true);
          }}
          loyaltyType={loyaltyToValidate}
          onCancel={() => setLoyaltyToValidate(undefined)}
        />
      ) : null}
      {showLoginRedirectionModal ? (
        <LoginRedirectModal
          onCancel={() => setShowLoginRedirectionModal(false)}
          loyaltyType={props.loyaltyType}
        />
      ) : null}
    </>
  );
};

export default PackageVariation;
